/**
 * @file Hover.tsx
 * @module Hover
 * @description Virzīšanās (hover) interakcijas pārvaldnieks Karšu pārlūkam.
 *              Klausās OpenLayers kartes pointermove notikumus un nosaka, vai objektam
 *              zem kursora ir pieejams hover pārklājums (SUP, GUP, CMS, kamera, VMS, UVIS).
 *              Pozicionē OpenLayers Overlay elementu pie kursora atrašanās vietas.
 *              Aktīvs tikai SIPR sistēmas režīmā.
 */
import { useContext, useEffect } from "react";
import { HoverManagerContext } from "./Hover-wrapper";
import { getMapStore } from "../Map.repository";
import { useObservable } from "@ngneat/use-observable";
import { Feature, Map, Overlay } from "ol";
import { HOVERLAY_LIST } from "./Hover-overlay-list";
import { Point } from "ol/geom";
import { checkIfVisible } from "../../data-layers/layer-switcher/Cluster/Cooler-cluster-style";
import RenderFeature from "ol/render/Feature";
import { getStyle } from "../overlay/Overlay";
import { OVERLAY_MANAGER } from "../overlay/Select";
import LayerGroup from "ol/layer/Group";
import { Positioning } from "ol/Overlay";

export const HOVERLAY_MANAGER = new Overlay({
	element: document.getElementById("hoverlay")!,
	offset: [0, 4],
});

export const Hover = () => {
	const { setContext } = useContext(HoverManagerContext);
	const [props] = useObservable(getMapStore()) as any;

	const addHover = (map: Map) => {
		return map.on("pointermove", (e) => {
			const lg = map
				.getLayers()
				.getArray()
				.find((lg) => lg instanceof LayerGroup && lg.get("type") === "base");
			const overview = lg?.getLayersArray().find((l) => l.getZIndex() === 100);
			if (overview) return;

			const features = map.getFeaturesAtPixel(e.pixel);

			const f = features[0];
			let feature: any = undefined;
			if (
				f &&
				(Object.keys(HOVERLAY_LIST).includes(f.get("layer")?.replace("public.", "") ?? f.get("internal")?.["table"]) ||
					!!Object.keys(HOVERLAY_LIST).find((key) => f.get("layer")?.includes(key)))
			) {
				feature = f;
			}

			if (!feature && features[0]?.get("features")) {
				const feats = features[0].get("features").filter((f: Feature) => checkIfVisible(f.get("internal"), map) && !!f.get("internal")?.["style"]);
				if (
					feats.length === 1 &&
					(Object.keys(HOVERLAY_LIST).includes(feats[0].get("layer").replace("public.", "")) ||
						!!Object.keys(HOVERLAY_LIST).find((key) => feats[0].get("layer")?.includes(key)))
				) {
					feature = feats[0];
				}
			}

			if (feature instanceof RenderFeature) {
				const style = getStyle(feature, feature.get("internal"));
				if (style.groupName === "Braukšanas apstākļu brīdinājumi") {
					return;
				}
				if (feature.get("layer").includes("kafkamessages_uvismessage") && (feature.getType() === "Polygon" || !style.use_uvis_popups)) {
					feature = undefined;
				}
			}

			if (feature && OVERLAY_MANAGER.getPosition() === undefined) {
				features.shift();
				setContext({ feature: feature as Feature, event: e });
				const baseCoord = feature instanceof Feature && feature.getGeometry()?.getType() === "Point" ? (feature.getGeometry() as Point).getCoordinates() : e.coordinate;

				const basePixel = map.getPixelFromCoordinate(baseCoord)!;
				const mapSize = map.getSize()!;

				const OVERLAY_W = 480;
				const OVERLAY_H = 480;

				const overflowsRight = basePixel[0] + OVERLAY_W > mapSize[0];
				const hasSpaceAbove = basePixel[1] - OVERLAY_H >= 0;
				const hasSpaceBelow = basePixel[1] + OVERLAY_H <= mapSize[1];
				const inBottomHalf = basePixel[1] > mapSize[1] / 2;

				const vertical = inBottomHalf
					? (hasSpaceBelow ? "top" : "bottom")
					: (hasSpaceAbove ? "bottom" : "top");
				const horizontal = overflowsRight ? "right" : "left";

				HOVERLAY_MANAGER.setPositioning(`${vertical}-${horizontal}` as Positioning);
				HOVERLAY_MANAGER.setPosition(baseCoord);
			} else {
				HOVERLAY_MANAGER.setPosition(undefined);
			}
		});
	};

	useEffect(() => {
		if (!props.map) return;

		props.map.addOverlay(HOVERLAY_MANAGER);
		const keyEvent = addHover(props.map);

		return () => props.map!.un("pointermove", keyEvent.listener);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.map]);

	HOVERLAY_MANAGER.setElement(document.getElementById("hoverlay")!);
	return <></>;
};
